import {
    Broadcast,
    CreatorProductEntitlementsBindingModelDiscriminator,
    LiveInputResult,
    SimulcastSwitcherStreamSetting
} from "@switcherstudio/switcher-api-client";
import { trackEvent } from "helpers/analyticsHelpers";
import rollbar from "helpers/rollbar";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useThumbnailUpload } from "hooks/useThumbnailUpload";
import {
    DestinationProcessingStatus,
    useDestinationConnections
} from "hooks/useDestinationConnections";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { PlatformId } from "views/page-content/platforms/types";
import CheckmarkIcon from "assets/icons/checkmark-filled.svg?react";
import ErrorIcon from "assets/icons/exclamation.svg?react";
import styles from "./index.module.scss";
import { useClaimCheck } from "hooks/useClaimCheck";
import { switcherSdk } from "utils/switcher-sdk";
import { SubmissionStatus } from "../../types";
import { ProcessingDestination } from "./ProcessingDestination";
import { motion } from "framer-motion";
import { useHasPlanRole } from "hooks/useHasPlanRole";

interface ProcessingStepProps {
    attemptId: number;
    isSubmitting: boolean;
    isCancelled: (attemptId: number) => boolean;
    setIsSubmitting: (attemptId: number, isSubmitting: boolean) => void;
    setSubmissionStatus: (attemptId: number, status: SubmissionStatus) => void;
    setOnAdvanceStep: (callback: () => void) => void;
}

// Re-exported for existing imports of DestinationProcessingStatus from this module.
export { DestinationProcessingStatus };

export const ProcessingStep = ({
    attemptId,
    isSubmitting,
    isCancelled,
    setIsSubmitting,
    setSubmissionStatus,
    setOnAdvanceStep
}: ProcessingStepProps) => {
    const hasGatedContentAccess = useClaimCheck("gatedcontent");
    const isStudioUser = useHasPlanRole("Studio");
    const { general, videoplayer } = useSelector(
        (state: RootState) => state.platforms
    );
    const { generalForm, selectedStreams } = general;
    const destinations = useMemo(
        () => generalForm?.selectedSimulcastDestinations ?? [],
        [generalForm?.selectedSimulcastDestinations]
    );
    const { ProjectId } = useSelector(
        (state: RootState) => state.user?.userInfo?.MetaData?.Details
    );

    const { handleUploadThumbnail } = useThumbnailUpload(
        generalForm.scheduledImageFile,
        generalForm.scheduledImageFileUrl
    );

    const handleThumbnailUpload = useCallback(async () => {
        if (!generalForm?.scheduledImageFile) return;
        try {
            return (await handleUploadThumbnail())?.Id;
        } catch (error) {
            rollbar.error("Thumbnail upload failed", error);
            throw new Error("Thumbnail upload failed");
        }
    }, [generalForm?.scheduledImageFile, handleUploadThumbnail]);

    const { dispatchApiRequest: createBroadcast } = useSwitcherClient(
        (client) => client.broadcasts_PostBroadcast,
        { suppressErrorBoundary: true }
    );

    const handleCreateBroadcast = useCallback(
        async (thumbnailAssetId: string) => {
            const catalogOrCollectionsSelected =
                generalForm.showInCatalog ||
                (videoplayer?.videoPlayerForm?.playlistIds?.length ?? 0) > 0;

            const broadcast: Broadcast = {
                Title: generalForm.title,
                Description: generalForm.description,
                ProjectId,
                EnableRecording: !isStudioUser || catalogOrCollectionsSelected,
                ThumbnailAssetId: thumbnailAssetId,
                StartsAt: generalForm.scheduledStartTime.toISOString(),
                EnableSwitcherPlayer:
                    generalForm.selectedSimulcastDestinations.some(
                        (dest) => dest.platform === PlatformId.VideoPlayer
                    ),
                EnableLiveShopping:
                    (videoplayer?.videoPlayerForm
                        ?.enableLiveShopping as boolean) ?? false,
                BroadcastWebLinks: videoplayer?.videoPlayerForm.links || [],
                ShowPremiereCountdown: generalForm.showPremiereCountdown,
                ShowInCatalog: generalForm.showInCatalog
            };

            try {
                const res = await createBroadcast([broadcast]);

                trackEvent("Event Created", {
                    platform: "Simulcast",
                    inDashboard: true,
                    broadcastId: res.Id,
                    name: generalForm.title,
                    scheduledForLater: true,
                    scheduledTime: generalForm.scheduledStartTime.toISOString(),
                    scheduledTimeFromNow: Math.floor(
                        (generalForm.scheduledStartTime.getTime() -
                            new Date().getTime()) /
                            1000
                    ),
                    existingScheduled:
                        selectedStreams?.facebook !== null ||
                        selectedStreams?.youtube !== null,
                    coverImageProvided: !!generalForm.scheduledImageFile,
                    collectionsSpecified:
                        videoplayer?.videoPlayerForm?.playlistIds?.length > 0
                });

                return res;
            } catch (error) {
                rollbar.error("Broadcast creation failed", error);
                throw new Error("Broadcast creation failed");
            }
        },
        [
            generalForm,
            ProjectId,
            videoplayer,
            createBroadcast,
            selectedStreams,
            isStudioUser
        ]
    );

    // Shared per-destination connection engine (sc-56801).
    const {
        postStatuses,
        postErrors,
        withStatus,
        seedDestinationForms,
        processDestination,
        retryDestination
    } = useDestinationConnections();

    // The broadcast created for this submission - lifted to state so a
    // per-destination retry (which happens after the initial submission
    // effect has finished) can still reference it.
    const [broadcastState, setBroadcastState] = useState<
        Broadcast | undefined
    >();

    // Whether adding the broadcast to the selected collections succeeded.
    // Collections aren't individually retryable, so this is only tracked
    // to compute the overall submission status below.
    const [collectionsSuccess, setCollectionsSuccess] = useState<boolean>(true);

    // A "hard" failure (thumbnail upload / broadcast creation / createInput /
    // initializeBroadcast) deletes the broadcast and is not recoverable via
    // per-destination retry - unlike destination failures, which are.
    const [hasHardFailure, setHasHardFailure] = useState<boolean>(false);

    const createPosts = useCallback(
        async (broadcast: Broadcast) => {
            const destinations = generalForm?.selectedSimulcastDestinations;
            if (!destinations?.length) return;

            seedDestinationForms(destinations);

            const requests = destinations
                .filter(() => !isCancelled(attemptId))
                .map((d) => withStatus(d.id, () => processDestination(d, broadcast)));

            await Promise.allSettled(requests);
        },
        [
            generalForm?.selectedSimulcastDestinations,
            seedDestinationForms,
            withStatus,
            processDestination,
            isCancelled,
            attemptId
        ]
    );

    /** Retries a single failed destination without re-running the rest of the batch. */
    const handleRetryDestination = useCallback(
        (destId: string) => {
            const destination = destinations.find((d) => d.id === destId);
            if (!destination || !broadcastState) return;

            retryDestination(destination, broadcastState);
        },
        [destinations, broadcastState, retryDestination]
    );

    const { dispatchApiRequest: postPlaylistBroadcast } = useSwitcherClient(
        (client) =>
            client.videoPlayerPlaylist_PostVideoPlayerPlaylistBroadcastById,
        { suppressErrorBoundary: true }
    );

    const { dispatchApiRequest: createEntitlements } = useSwitcherClient(
        (client) => client.creatorProductEntitlements_Create,
        { suppressErrorBoundary: true }
    );

    const handleAddToCollections = useCallback(
        async (broadcastId: string) => {
            const collectionIds =
                videoplayer?.videoPlayerForm?.playlistIds?.filter(
                    (id): id is string => !!id
                );

            // Nothing to add - not a failure, so callers must treat this as
            // success (previously returned `undefined`, which is falsy and
            // caused a single failed destination with zero collections
            // selected to look like a total failure - see report).
            if (!collectionIds || collectionIds.length === 0) return true;

            const productId = videoplayer?.videoPlayerForm?.creatorProductId;

            // One task per playlist, with contextualized try/catch inside.
            const tasks = collectionIds.map(async (playlistId) => {
                try {
                    // 1) Create/attach the broadcast to the playlist
                    const playlistBroadcastResponse =
                        await postPlaylistBroadcast([playlistId, broadcastId]);

                    const playlistBroadcastId =
                        playlistBroadcastResponse?.PlaylistBroadcast?.Id;

                    if (!playlistBroadcastId) {
                        throw new Error(
                            `Missing PlaylistBroadcast.Id for playlist ${playlistId}`
                        );
                    }

                    // 2) If gated, create entitlements
                    if (hasGatedContentAccess && productId) {
                        await createEntitlements([
                            {
                                ProductEntitlements: [
                                    {
                                        ProductId: productId,
                                        VideoPlayerPlaylistBroadcastId:
                                            playlistBroadcastId,
                                        Discriminator:
                                            CreatorProductEntitlementsBindingModelDiscriminator._2
                                    }
                                ]
                            }
                        ]);
                    }
                } catch (err) {
                    // Add rich context, then rethrow so Promise.allSettled can capture it.
                    const message =
                        err instanceof Error
                            ? err.message
                            : String(err ?? "Unknown error");
                    throw new Error(
                        `Failed to add broadcast ${broadcastId} to playlist ${playlistId}: ${message}`,
                        { cause: err instanceof Error ? err : undefined }
                    );
                }
            });

            // Run all tasks; collect any failures; throw once with details.
            const results = await Promise.allSettled(tasks);
            const errors = results
                .filter(
                    (r): r is PromiseRejectedResult => r.status === "rejected"
                )
                .map((r) => r.reason);

            return errors.length === 0;
        },
        [
            videoplayer,
            postPlaylistBroadcast,
            createEntitlements,
            hasGatedContentAccess
        ]
    );

    const { dispatchApiRequest: createInput } = useSwitcherClient(
        (client) => client.broadcasts_CreateInput,
        { suppressErrorBoundary: true }
    );

    const { dispatchApiRequest: initialize } = useSwitcherClient(
        (client) => client.broadcasts_InitializeBroadcast,
        { suppressErrorBoundary: true }
    );

    const initializeBroadcast = useCallback(
        async (broadcastId: string, input: LiveInputResult) => {
            const setting: SimulcastSwitcherStreamSetting = {
                ...generalForm.selectedQuality.setting
            };
            setting["channel-name"] = generalForm.title;
            const streamSetting = await initialize([
                broadcastId,
                { input, setting }
            ]);

            return streamSetting;
        },
        [generalForm, initialize]
    );

    const { dispatchApiRequest: deleteBroadcast } = useSwitcherClient(
        (client) => client.broadcasts_DeleteBroadcast,
        { suppressErrorBoundary: true }
    );

    const deleteCancelledBroadcast = useCallback(
        async (broadcastId: string) => {
            try {
                await deleteBroadcast([broadcastId]);
            } catch (e) {
                rollbar.error("Failed to delete cancelled broadcast", e, {
                    broadcastId
                });
            }
        },
        [deleteBroadcast]
    );

    const createSimulcastBroadcast = useCallback(async () => {
        let thumbnailAssetId: string | undefined;
        let broadcast: Broadcast | undefined;
        let streamSetting: SimulcastSwitcherStreamSetting | undefined;

        try {
            thumbnailAssetId = await handleThumbnailUpload();
            // Cancelled before the broadcast exists - nothing to clean up.
            if (isCancelled(attemptId)) return;

            broadcast = await handleCreateBroadcast(thumbnailAssetId);
            // Cancel landed while the broadcast was being created - delete it.
            if (isCancelled(attemptId)) {
                await deleteCancelledBroadcast(broadcast.Id);
                return;
            }
            // Lift the broadcast to state so a later per-destination retry
            // (which happens well after this function returns) can use it.
            setBroadcastState(broadcast);

            try {
                const input = await createInput([broadcast.Id]);
                if (isCancelled(attemptId)) {
                    await deleteCancelledBroadcast(broadcast.Id);
                    return;
                }

                await createPosts(broadcast);
                if (isCancelled(attemptId)) {
                    await deleteCancelledBroadcast(broadcast.Id);
                    return;
                }

                const collectionsResultsSuccess = await handleAddToCollections(
                    broadcast.Id
                );
                if (isCancelled(attemptId)) {
                    await deleteCancelledBroadcast(broadcast.Id);
                    return;
                }
                setCollectionsSuccess(collectionsResultsSuccess);

                streamSetting = await initializeBroadcast(broadcast.Id, input);
                if (isCancelled(attemptId)) {
                    await deleteCancelledBroadcast(broadcast.Id);
                    return;
                }

                // Destination failures no longer delete the broadcast or throw -
                // they're recoverable via retry (see ProcessingDestination). The
                // overall SubmissionStatus (Success vs Mixed) is derived reactively
                // from postStatuses/collectionsSuccess below, so it stays correct
                // as the user retries individual destinations.
            } catch (e) {
                // Only createInput/initializeBroadcast (or an unexpected throw)
                // land here - these are hard failures, unlike a destination
                // failing to connect, so the broadcast is not recoverable.
                await deleteBroadcast([broadcast.Id]);
                setHasHardFailure(true);
                setBroadcastState(undefined);
                throw e;
            } finally {
                if (
                    switcherSdk.clientId !== "Generic" &&
                    switcherSdk.api.versionMajor >= 2
                ) {
                    return switcherSdk.terminateBCProfileCreation(
                        streamSetting
                    );
                }
            }
        } catch (e) {
            rollbar.error("Simulcast setup failed", e, {
                thumbnailAssetId,
                broadcastId: broadcast?.Id,
                streamSetting
            });
            setSubmissionStatus(attemptId, SubmissionStatus.Failure);
        } finally {
            setIsSubmitting(attemptId, false);
        }
    }, [
        attemptId,
        isCancelled,
        handleThumbnailUpload,
        setIsSubmitting,
        handleCreateBroadcast,
        handleAddToCollections,
        createInput,
        createPosts,
        initializeBroadcast,
        deleteBroadcast,
        deleteCancelledBroadcast,
        setSubmissionStatus
    ]);

    useEffect(() => {
        createSimulcastBroadcast();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recomputes the overall SubmissionStatus whenever destination statuses
    // change - both right after the initial submission and after each retry.
    // Skipped for hard failures, which set Failure directly above and are not
    // recoverable, so there's nothing to recompute.
    useEffect(() => {
        if (isSubmitting || hasHardFailure) return;

        if (destinations.length === 0) {
            setSubmissionStatus(
                attemptId,
                collectionsSuccess
                    ? SubmissionStatus.Success
                    : SubmissionStatus.Mixed
            );
            return;
        }

        const statuses = destinations.map((d) => postStatuses[d.id]);
        const stillProcessing = statuses.some(
            (s) => !s || s === DestinationProcessingStatus.Processing
        );
        if (stillProcessing) return;

        const allDestinationsSuccessful = statuses.every(
            (s) => s === DestinationProcessingStatus.Success
        );

        setSubmissionStatus(
            attemptId,
            allDestinationsSuccessful && collectionsSuccess
                ? SubmissionStatus.Success
                : SubmissionStatus.Mixed
        );
    }, [
        attemptId,
        isSubmitting,
        hasHardFailure,
        destinations,
        postStatuses,
        collectionsSuccess,
        setSubmissionStatus
    ]);

    // BACK from the error/mixed state must delete the broadcast before
    // returning to LivestreamDetails, so re-submitting creates a clean one.
    // CANCEL deletes the broadcast (or compensates whatever step it interrupted)
    // via the checkpoints in createSimulcastBroadcast above.
    useEffect(() => {
        const handleAdvance = () => {
            if (isSubmitting || hasHardFailure || !broadcastState?.Id) return;
            if (
                destinations.length > 0 &&
                destinations.every(
                    (d) =>
                        postStatuses[d.id] ===
                        DestinationProcessingStatus.Success
                ) &&
                collectionsSuccess
            ) {
                // Everything succeeded - this is the "Schedule Another" path,
                // not a "Back" - nothing to delete.
                return;
            }

            deleteBroadcast([broadcastState.Id]).catch((err) => {
                rollbar.error(
                    "Failed to delete broadcast when navigating back from partial failure",
                    err,
                    { broadcastId: broadcastState.Id }
                );
            });
        };

        setOnAdvanceStep(() => handleAdvance);
    }, [
        setOnAdvanceStep,
        isSubmitting,
        hasHardFailure,
        broadcastState,
        destinations,
        postStatuses,
        collectionsSuccess,
        deleteBroadcast
    ]);

    const { allSuccessful, allWrong } = useMemo(() => {
        const allStatuses = destinations.map((destination) => {
            return (
                postStatuses[destination.id] ||
                DestinationProcessingStatus.Processing
            );
        });

        return {
            allSuccessful: allStatuses.every(
                (status) => status === DestinationProcessingStatus.Success
            ),
            allWrong: allStatuses.every(
                (status) => status === DestinationProcessingStatus.Error
            )
        };
    }, [destinations, postStatuses]);

    const headerContent = useMemo(() => {
        if (isSubmitting) {
            return (
                <>
                    <h6>Connecting...</h6>
                    <p>
                        Please standby as we configure your selected
                        destinations. See details below.
                    </p>
                </>
            );
        }

        if (allSuccessful) {
            return (
                <>
                    <CheckmarkIcon className={styles.checkmark} />
                    <h6>Your Livestream is Scheduled</h6>
                    <p>All destinations successfully configured.</p>
                </>
            );
        }

        if (allWrong) {
            return (
                <>
                    <ErrorIcon className={styles.exclamation} />
                    <h6>Something Went Wrong</h6>
                    <p>An error occurred when scheduling your livestream.</p>
                </>
            );
        }

        if (!allSuccessful && !allWrong) {
            return (
                <>
                    <h6>Attention</h6>
                    <p>
                        Some destinations could not be properly configured. See
                        details below.
                    </p>
                </>
            );
        }
    }, [isSubmitting, allSuccessful, allWrong]);

    const orderedDestinations = useMemo(() => {
        return [...destinations].sort((a, b) => {
            // Sort by status: Success > Failure > Processing
            const statusOrder = {
                [DestinationProcessingStatus.Success]: 0,
                [DestinationProcessingStatus.Error]: 1,
                [DestinationProcessingStatus.Processing]: 2
            };
            const statusA =
                postStatuses[a.id] || DestinationProcessingStatus.Processing;
            const statusB =
                postStatuses[b.id] || DestinationProcessingStatus.Processing;

            // Then by name alphabetically
            if (statusA === statusB) {
                return a.name.localeCompare(b.name);
            }

            return statusOrder[statusA] - statusOrder[statusB];
        });
    }, [destinations, postStatuses]);

    return (
        <section className={styles["processing-step"]}>
            <header>{headerContent}</header>

            {orderedDestinations.length > 0 && (
                <motion.ul
                    className={styles["destination-list"]}
                    layout
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                >
                    {orderedDestinations.map((destination) => (
                        <motion.li
                            key={destination.id}
                            layout
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                        >
                            <ProcessingDestination
                                destination={destination}
                                status={
                                    postStatuses[destination.id] ??
                                    DestinationProcessingStatus.Processing
                                }
                                error={postErrors[destination.id]}
                                onRetry={
                                    hasHardFailure
                                        ? undefined
                                        : handleRetryDestination
                                }
                            />
                        </motion.li>
                    ))}
                </motion.ul>
            )}
        </section>
    );
};
